import React, { createContext, useCallback, useContext, useState } from 'react';

type BackHandler = (() => void) | null;

interface AppShellContextValue {
  breadcrumb: string[];
  setBreadcrumb: (b: string[]) => void;
  topBarActions: React.ReactNode | null;
  setTopBarActions: (n: React.ReactNode | null) => void;
  /** Optional back handler. When set, the top bar shows a Back button wired to it. */
  topBarBack: BackHandler;
  setTopBarBack: (fn: BackHandler) => void;
}

export const AppShellContext = createContext<AppShellContextValue | null>(null);

export function AppShellProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [breadcrumb, setBreadcrumb] = useState<string[]>([]);
  const [topBarActions, setTopBarActions] = useState<React.ReactNode | null>(null);
  const [topBarBack, setTopBarBackState] = useState<BackHandler>(null);

  // Wrap in the functional updater form so a stored function isn't mistaken
  // for a state-updater callback by React's setState.
  const setTopBarBack = useCallback((fn: BackHandler) => {
    setTopBarBackState(() => fn);
  }, []);

  return (
    <AppShellContext.Provider
      value={{ breadcrumb, setBreadcrumb, topBarActions, setTopBarActions, topBarBack, setTopBarBack }}
    >
      {children}
    </AppShellContext.Provider>
  );
}

const NO_OP_CONTEXT: AppShellContextValue = {
  breadcrumb: [],
  setBreadcrumb: () => {},
  topBarActions: null,
  setTopBarActions: () => {},
  topBarBack: null,
  setTopBarBack: () => {},
};

export function useAppShell(): AppShellContextValue {
  return useContext(AppShellContext) ?? NO_OP_CONTEXT;
}
